import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } })
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const ContactUs = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:info@nistalasoh.com?subject=${encodeURIComponent(form.subject || 'Contact Inquiry')}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`;
    window.open(mailtoLink, '_blank');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const contactInfo = [
    { icon: Mail, title: 'Email', value: 'info@nistalasoh.com', link: 'mailto:info@nistalasoh.com' },
    { icon: Phone, title: 'Toll Free', value: '1800-5693-4335', link: 'tel:180056934335' },
    { icon: MapPin, title: 'Location', value: 'Hyderabad, India', link: null },
  ];

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="contact-page">
      <SEO title="Contact Us" description="Reach out to Nistala's Group for partnerships, franchise inquiries, or general questions. Based in Hyderabad, India." />
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#2C1810]" />
        <div className="relative max-w-5xl mx-auto px-4 text-center pt-16 pb-4">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-xs tracking-[0.25em] uppercase text-[#D4A574] mb-4">Get in Touch</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contact <span className="text-[#D4A574]">Us</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="text-base text-white/70 max-w-xl mx-auto">
            Have questions, partnership inquiries, or feedback? We'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                <motion.h2 variants={fadeUp} className="text-2xl font-bold text-[#2C1810] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Let's Connect</motion.h2>
                <motion.p variants={fadeUp} custom={1} className="text-sm text-[#7a6555] leading-relaxed mb-6">
                  Whether you're a farmer looking to join our network, a business seeking partnerships, or a customer with questions — we're here to help.
                </motion.p>
              </motion.div>

              {contactInfo.map((info, idx) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#5C7A3A]/10 flex items-center justify-center flex-shrink-0">
                    <info.icon size={18} className="text-[#5C7A3A]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#7a6555] uppercase tracking-wide">{info.title}</p>
                    {info.link ? (
                      <a href={info.link} className="text-sm font-medium text-[#2C1810] hover:text-[#5C7A3A] transition-colors">{info.value}</a>
                    ) : (
                      <p className="text-sm font-medium text-[#2C1810]">{info.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Form */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#E8DFD3] shadow-sm p-6 space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-[#7a6555] mb-1 block">Your Name</label>
                    <Input
                      required
                      placeholder="Enter your name"
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      className="border-[#E8DFD3]"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-[#7a6555] mb-1 block">Email Address</label>
                    <Input
                      required
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      className="border-[#E8DFD3]"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#7a6555] mb-1 block">Subject</label>
                  <Input
                    placeholder="What is this regarding?"
                    value={form.subject}
                    onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                    className="border-[#E8DFD3]"
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#7a6555] mb-1 block">Message</label>
                  <textarea
                    required
                    rows={5}
                    placeholder="Tell us how we can help..."
                    value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full border border-[#E8DFD3] rounded-lg px-4 py-2.5 text-sm bg-white text-[#2C1810] focus:outline-none focus:ring-2 focus:ring-[#D4A574]/40 focus:border-[#D4A574] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white font-medium px-8 cursor-pointer"
                  data-testid="contact-submit-btn"
                >
                  <Send size={15} className="mr-2" />
                  {submitted ? 'Email Client Opened!' : 'Send Message'}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
